import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminLayout } from './components/layout';
import { Toaster } from './components/ui/toaster';
import { SearchProvider } from './contexts/SearchContext';

import LoginPage from './components/login';
import UserShow from './components/users/user.view';
import UserList from './components/users/users';
// import UserEdit from "./components/users/user.edit";
// Legacy full-page create form — use Add Asset modal on /assets instead
// import NFTCreate from './components/nft/nft.create';
import NFTEdit from './components/nft/nft.edit';
import NFTShow from './components/nft/nft.view';
import AssetList from './components/nft/nfts';
// import NFTEdit from "./components/nft/nft.edit";
import RentalList from './components/nft/rental';
import NotFound from './components/notFound';
import RentalShow from './components/rental/rental.view';
import UpdateYields from './components/yield-details/update-yields';
// import UpdateResellPrice from './components/resell-price/update-resell-price';
import AmenitiesManagement from './components/amenities/amenities';
import { Dashboard } from './components/dashboard/Dashboard';
import CompliancePage from './components/compliance/compliance';
import { SearchResults } from './components/search/SearchResults';
import Reports from './pages/Reports';
import VestnInsightAI from './pages/VestnInsightAI';
import Wallets from './pages/Wallets';
import Settings from './pages/Settings';
import AuditLogs from './pages/AuditLogs';
import ErrorBoundary from './components/ErrorBoundary';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import { toast } from './components/ui/use-toast';
import { getTransactionErrorMessage } from './utils/errorMessages';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
    };

    // Check authentication on mount
    checkAuth();

    // Also check on storage changes (e.g., when token is set/removed in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Handle unhandled MetaMask/wallet transaction rejections (e.g. user denies signature)
  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const msg =
        reason != null && typeof reason === 'object' && 'message' in reason
          ? String((reason as { message?: unknown }).message)
          : String(reason);
      const isWalletDenial =
        /user denied|denied transaction|rejected by user|user rejected/i.test(msg) ||
        /MetaMask Tx Signature/i.test(msg);
      if (isWalletDenial) {
        event.preventDefault();
        toast({
          description: getTransactionErrorMessage(reason),
          variant: 'destructive',
        });
      }
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', onUnhandledRejection);
  }, []);

  const handleLogin = () => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  };

  useInactivityLogout(isAuthenticated);

  if (!isAuthenticated) {
    return (
      <>
        <Toaster />
        <LoginPage onLogin={handleLogin} />
      </>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SearchProvider>
          <Toaster />
          <AdminLayout>
            <Routes>
              <Route path='/' element={<Navigate to='/dashboard' replace />} />
              <Route path='/dashboard' element={<Dashboard />} />
              <Route path='/search' element={<SearchResults />} />
              <Route path='/users' element={<UserList />} />
              <Route path='/users/:id' element={<UserShow />} />
              <Route path='/assets' element={<AssetList />} />
              <Route path='/assets/:id' element={<NFTShow />} />
              <Route path='/compliance' element={<CompliancePage />} />
              <Route path='/amenities' element={<AmenitiesManagement />} />
              <Route path='/assets/create' element={<Navigate to='/assets?openCreate=1' replace />} />
              <Route path='/assets/:id/edit' element={<NFTEdit />} />
              <Route path='/update-yields' element={<UpdateYields />} />
              {/* <Route path='/update-resell-price' element={<UpdateResellPrice />} /> */}
              <Route path='/rental' element={<RentalList />} />
              <Route path='/rental/:id' element={<RentalShow />} />
              <Route path='/reports' element={<Reports />} />
              <Route path='/insight-ai' element={<VestnInsightAI />} />
              <Route path='/audit-logs' element={<AuditLogs />} />
              <Route path='/wallets' element={<Wallets />} />
              <Route path='/settings' element={<Settings />} />
              <Route path='*' element={<NotFound />} />
            </Routes>
          </AdminLayout>
        </SearchProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
